import { css } from '@emotion/react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck, faCircleQuestion,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  Breakpoint, breakpoints, mq,
} from '~/_shared/style.mixins';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';

const ICON_TEXT_GAP = 20;
const MAX_ICON_DIMENSION = 28;

const containerStyle = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

const confirmationWrapperStyle = (theme: Theme) => css({
  display: 'flex',
  justifyContent: 'space-between',
  color: theme.textColors.light,
  backgroundColor: theme.alertColors.infoSecondary,
  width: '100%',
});

const contentWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '16px 16px',
  gap: 20,
  flexWrap: 'wrap',
  [mq(breakpoints[Breakpoint.mid])]: {
    flexWrap: 'nowrap',
  },
});

const iconTextStyle = css({
  cursor: 'default',
  display: 'flex',
  alignItems: 'center',
});

const iconWrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
});

const textStyle = css({
  fontSize: 16,
  margin: `0 0 0 ${ICON_TEXT_GAP}px`,
});

const iconStyle = (theme: Theme) => css({
  color: theme.textColors.light,
  fontSize: MAX_ICON_DIMENSION,
});

const buttonsContainerStyle = css({
  display: 'flex',
  gap: '10px',
  justifyContent: 'end',
  flexGrow: 1,
});

type TopUnderbarConfirmationProps = Readonly<{
  icon?: IconProp;
  text: string;
  onConfirm: () => void;
  onDeny: () => void;
}>;

export const TopUnderbarConfirmationComponent: FC<TopUnderbarConfirmationProps> = (props) => {
  const [t] = useTranslation();
  const { icon, text, onConfirm, onDeny } = props;

  return (
    <div css={containerStyle}>
      <div css={confirmationWrapperStyle}>
        <div css={contentWrapperStyle}>
          <div css={iconTextStyle}>
            <div css={iconWrapperStyle}>
              <FontAwesomeIcon
                css={iconStyle}
                icon={icon ? icon : faCircleQuestion}
              />
            </div>
            <p css={textStyle}>
              {text}
            </p>
          </div>

          <div css={buttonsContainerStyle}>
            <ButtonComponent
              prefixIcon={faXmark}
              text={t('Reject')}
              buttonStyle={ButtonStyle.Danger}
              onClick={onDeny}
            />
            <ButtonComponent
              prefixIcon={faCheck}
              text={t('Accept')}
              buttonStyle={ButtonStyle.Success}
              onClick={onConfirm}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const renderConfirmation = ({
  icon,
  id,
  text,
  onConfirm,
  onDeny,
}: {
  icon?: IconProp;
  id: string;
  text: string;
  onConfirm: () => void;
  onDeny: () => void;
}) => {
  return ({
    key: `confirmation_${id}`,
    children: (
      <TopUnderbarConfirmationComponent
        icon={icon}
        text={text}
        onConfirm={onConfirm}
        onDeny={onDeny}
      />
    ),
    // No onClose handler since this is handled by onConfirm/onDeny
    padding: 0,
    margin: 0,
  });
};
